import { useEffect, useRef, useState } from "react";
import { TenAmRecordPanel } from "@/components/desk/TenAmRecord";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Archive, GraduationCap } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, fmtErr } from "@/lib/api";
import { RUN_META, checkpointSchedule } from "@/lib/format";
import { QueryError, UpdatedAt, isNoSession, retryTransient } from "@/lib/queryState";
import { LIVE_REFETCH_MS, useLiveWindow } from "@/lib/marketHours";
import { currentDecision, decisionRuns, isLate, isScoringOnly, lateLabel, pCloseModeled, postCloseReview, skewUsable } from "@/lib/checkpointState";
import { decisionReason, enterOffLine, enterOffPassed, noBlockingRuleText, safetyBlockDetail, safetyBlockText } from "@/lib/decisionText";
import { AuditRow, blockingProps, pathOf } from "@/components/desk/DeskAudit";
import { AckButton, DeskTradeTicket } from "@/components/desk/DeskTicket";
import { CandidateBlotter } from "@/components/desk/CandidateBlotter";
import { RunControls } from "@/components/desk/RunControls";
import { EntryReadinessChecklist } from "@/components/EntryReadinessChecklist";
import PayoffDiagram from "@/components/PayoffDiagram";
import { PrintBar } from "@/components/PrintBar";
import GuidedTour from "@/components/GuidedTour";
import { SkewSparkline } from "@/components/SkewSparkline";
import { SignalExplainer } from "@/components/SignalExplainer";
import { SoftAdvisoryStrip } from "@/components/SoftAdvisoryStrip";
import { PaperStrategyNote } from "@/components/desk/PaperStrategies";
import { CheckpointTime } from "@/components/CheckpointTime";
import {
  Badge, BlockingRuleCallout, Button, CheckpointTimeline, DataBanner, DataStateBadge, DecisionHero, EmptyState, Icon,
  IconButton, PageHead, PageLoading, Panel, PathChip, RuleResult, SegmentedControl, StatCell, SystemBadge, etSyd, fmt, fmtDuration,
  normDecision, sessionDateLabel, shortId, timeET, useNow, useViewport, etAndLocal } from "@/sentinel";

/** "PRE-MARKET BUILD" → "Pre-market build" (sentence case for names; the vocabulary stays uppercase). */
const sentence = (s) => { const t = String(s || "").toLowerCase(); return t.charAt(0).toUpperCase() + t.slice(1); };
const isEnter = (d) => d === "ENTER" || d === "ENTER PRE-OPEN";

/**
 * Scroll the nearest scrolling ancestor so `el` is at its top. (scrollIntoView would also scroll the
 * document behind the shell's fixed layout.)
 */
function scrollWithin(el) {
  let box = el.parentElement;
  while (box && box !== document.body) {
    const oy = getComputedStyle(box).overflowY;
    if ((oy === "auto" || oy === "scroll") && box.scrollHeight > box.clientHeight) break;
    box = box.parentElement;
  }
  const top = (b) => b.getBoundingClientRect().top;
  if (!box || box === document.body) window.scrollTo({ top: top(el) + window.scrollY - 16 });
  else box.scrollTo({ top: top(el) - top(box) + box.scrollTop - 16 });
}

/** The session's mode, for the one-line status and the data badge. */
function sessionMode(win, selectedDate) {
  if (win.live) return { key: "live", label: "Live", badge: <DataStateBadge state="LIVE" data-testid="desk-mode-badge" /> };
  if (win.isToday) return { key: "saved", label: "Saved", badge: <Badge icon="Lock" data-testid="desk-mode-badge">SAVED SESSION</Badge> };
  if (selectedDate && selectedDate > win.today) return { key: "scheduled", label: "Scheduled", badge: <Badge icon="CalendarDays" data-testid="desk-mode-badge">SCHEDULED</Badge> };
  return { key: "archived", label: "Archived", badge: <Badge icon={Archive} data-testid="desk-mode-badge">ARCHIVED</Badge> };
}

function NextCheckpoint({ selectedDate, win, doneRuns, review }) {
  const now = useNow(1000);
  if (!win.isToday) {
    return <p className="sn-small sn-muted" style={{ margin: 0 }} data-testid="review-countdown-context">No countdown for an archived session.{review ? " Post-close review recorded." : ""}</p>;
  }
  const schedule = [1, 2, 3, 4, 5, 6].map((run) => ({ run, ...checkpointSchedule(selectedDate, RUN_META[run].time) }));
  const next = schedule.find((s) => s.at > now.getTime());
  if (!next) return <p className="sn-small sn-muted" style={{ margin: 0 }}>{doneRuns.has(6) ? "Post-close review recorded." : "Today's cutoffs have passed."}</p>;
  const remaining = Math.ceil((next.at - now.getTime()) / 1000);
  const prev = schedule.filter((s) => s.at && s.at <= now.getTime()).pop();
  const progress = prev ? Math.min(1, Math.max(0, (now.getTime() - prev.at) / (next.at - prev.at))) : 0;
  return (
    <div className="sn-cd" data-testid="next-checkpoint">
      <span className="sn-stat-k">Next run · R{next.run}</span>
      <span className="sn-cd-v sn-num" role="timer" aria-live="off" aria-label={`Run ${next.run} in ${fmtDuration(remaining)}`} data-testid="countdown-value">{fmtDuration(remaining)}</span>
      <span className="sn-cd-bar" aria-hidden="true"><i style={{ width: `${progress * 100}%` }} /></span>
      <span className="sn-help">{next.run === 6 ? "Post-close review" : sentence(RUN_META[next.run].name)}{doneRuns.has(next.run) ? " · already run" : ""}</span>
      <CheckpointTime sessionDate={selectedDate} cutoffET={RUN_META[next.run].time} testId="next-checkpoint-time" />
    </div>
  );
}

/** CheckpointTimeline nodes: R1–R6. Run 6 is the post-close review and never shows a decision. */
function timelineFrom(checkpoints, currentRun) {
  const originals = checkpoints.filter((c) => !c.recalc_of);
  return [1, 2, 3, 4, 5, 6].map((run) => {
    const cp = originals.find((c) => c.run === run);
    const recalcs = checkpoints.filter((c) => c.recalc_of && c.run === run).sort((a, b) => (a.timestamp_et < b.timestamp_et ? -1 : 1));
    const last = recalcs[recalcs.length - 1];
    const scoring = run === 6 || isScoringOnly(cp);
    return {
      id: "R" + run,
      time: cp?.cutoff_et || RUN_META[run].time,
      name: `${scoring ? "Post-close review" : sentence(RUN_META[run].name)}${isLate(cp) ? " · LATE" : ""}`,
      s: cp ? (run === currentRun ? "current" : "frozen") : "pending",
      decision: cp && !scoring ? normDecision(cp.decision_label) : null,
      recalc: last ? { id: `RECALC ×${recalcs.length}`, time: timeET(last.timestamp_et, { seconds: false }), reason: `Latest recalculation at ${etSyd(last.timestamp_et)}; the original R${run} is untouched.` } : null,
    };
  });
}

/** An expandable section: a heading row with a toggle, content only when open. */
function Collapsible({ id, title, icon, meta, defaultOpen = false, children, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Panel id={id} title={title} icon={icon} data-testid={testId}
      meta={meta}
      actions={<Button size="sm" variant="ghost" icon={open ? "ChevronUp" : "ChevronDown"} aria-expanded={open} aria-controls={`${id}-body`} onClick={() => setOpen((o) => !o)} data-testid={`${testId}-toggle`}>{open ? "Hide" : "Show"}</Button>}>
      {open ? <div id={`${id}-body`} className="sn-stack">{children}</div> : null}
    </Panel>
  );
}

/**
 * The Session desk (/tonight, and /primary, /candidates scrolled to their sections).
 * Decision first, then the ENTER ticket or the blocking rule on the nearest failed candidate,
 * entry readiness, and the ranked blotter. One run selector drives every section.
 */
export default function SessionDesk({ focus }) {
  const { selectedDate, setSelectedDate, can, health } = useAuth();
  const { sessionState } = useOutletContext() || {};
  // Viewer-readable ENTER state from /health. Missing on an older backend: then say nothing.
  const enter = health?.enter;
  const enterLine = enterOffLine(enter);
  const { mobile, width } = useViewport();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const win = useLiveWindow(selectedDate);

  const [onboardHidden, setOnboardHidden] = useState(() => { try { return localStorage.getItem("sentinel_onboarded") === "1"; } catch { return true; } });
  const dismissOnboard = () => { try { localStorage.setItem("sentinel_onboarded", "1"); } catch { /* private mode */ } setOnboardHidden(true); };
  const [tourOn, setTourOn] = useState(false);
  useEffect(() => {
    let done = true;
    try { done = localStorage.getItem("sentinel_tour_done") === "1"; } catch { /* private mode */ }
    if (!done && can("analyst")) {
      const t = setTimeout(() => setTourOn(true), 900);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [can]);
  const endTour = () => { try { localStorage.setItem("sentinel_tour_done", "1"); } catch { /* private mode */ } setTourOn(false); };
  const TOUR_STEPS = [
    { testid: "tenam-panel", title: "The 10:00 am record", body: `The main research line. At ${etAndLocal("10:01:15")} the desk records the 10:00 level and whether the frozen quiet-day filter keeps the session; after the close it adds what happened. Research only, never a signal.` },
    { testid: "dashboard-decision-pill", title: "The old rule set's call", body: "The decision from the rules the desk was first built on, kept for comparison. No test found an edge after costs, so ENTER stays off and a setup that passes every rule shows as WAIT." },
    { testid: "desk-ticket", title: "Why, or what to do", body: "On ENTER the read-only ticket and exit plan appear here. Otherwise, the rule that kept the nearest candidate out, in plain words." },
    { testid: "run-controls", title: "Run controls", body: "Start live and manual runs live here. Every run and RECALC asks you to confirm first." },
    { testid: null, title: "Explore", body: "Use the left rail (the tab bar on a phone) for History, Scoring and Backtest. The Guide explains the terms." },
  ];

  const sessionQ = useQuery({ queryKey: ["session", selectedDate], queryFn: () => api.getSession(selectedDate), enabled: !!selectedDate, retry: retryTransient });
  const cpsQ = useQuery({
    queryKey: ["checkpoints", selectedDate], queryFn: () => api.getCheckpoints(selectedDate), enabled: !!selectedDate,
    refetchInterval: win.polling ? LIVE_REFETCH_MS : false, retry: retryTransient,
  });
  const { data: risk } = useQuery({ queryKey: ["risk"], queryFn: api.getRisk, enabled: can("analyst"), retry: false });
  const session = sessionQ.data;
  const checkpoints = cpsQ.data || [];

  const runs = decisionRuns(checkpoints);
  const current = currentDecision(checkpoints);
  const review = postCloseReview(checkpoints);
  const doneRuns = new Set(checkpoints.filter((c) => !c.recalc_of).map((c) => c.run));
  const pinned = Number(params.get("run")) || null;
  const selectedRun = runs.some((c) => c.run === pinned) ? pinned : current?.run ?? null;
  const cp = runs.find((c) => c.run === selectedRun) || null;
  const viewingCurrent = !!cp && cp.id === current?.id;

  // A new sealed checkpoint (seen here or by the header's session poll) refreshes everything derived from it.
  const sealedCount = Math.max(checkpoints.length, sessionState?.checkpoints?.length || 0);
  const prevCount = useRef(sealedCount);
  useEffect(() => {
    if (prevCount.current !== sealedCount) {
      prevCount.current = sealedCount;
      ["checkpoints", "primary", "candidates", "entry-readiness", "live-signals"].forEach((k) => qc.invalidateQueries({ queryKey: [k, selectedDate] }));
    }
  }, [sealedCount, selectedDate, qc]);

  const primaryRun = selectedRun && selectedRun >= 3 && selectedRun <= 5 ? selectedRun : null;
  const primaryQ = useQuery({ queryKey: ["primary", selectedDate, primaryRun], queryFn: () => api.getPrimary(selectedDate, primaryRun), enabled: !!selectedDate && !!primaryRun, retry: retryTransient });
  const candQ = useQuery({ queryKey: ["candidates", selectedDate, selectedRun], queryFn: () => api.getCandidates(selectedDate, selectedRun), enabled: !!selectedDate && !!selectedRun, retry: retryTransient });

  // Deep links: /primary → the ticket, /candidates → the blotter, or #desk-ticket / #desk-candidates.
  const target = focus || (location.hash ? location.hash.slice(1).replace(/^desk-/, "") : null);
  const scrolled = useRef(null);
  useEffect(() => {
    if (!target || !cp || scrolled.current === `${target}-${selectedDate}`) return;
    const el = document.getElementById(`desk-${target}`);
    if (el) { scrolled.current = `${target}-${selectedDate}`; scrollWithin(el); }
  }, [target, cp, selectedDate, primaryQ.data, candQ.data]);

  const pickRun = (n) => {
    const next = new URLSearchParams(params);
    if (n === current?.run) next.delete("run"); else next.set("run", String(n));
    setParams(next, { replace: true });
  };

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["checkpoints", selectedDate] });
    qc.invalidateQueries({ queryKey: ["session", selectedDate] });
  };

  const noSession = sessionQ.isError && isNoSession(sessionQ.error);
  const isHoliday = session?.status === "holiday";
  const isSkipped = session?.status === "skipped";
  const killArmed = !!session?.kill_switch;
  const stub = sessionState?.provenance === "STUB" || (session && session.provider !== "polygon");
  const numState = stub ? "stub" : undefined;
  const mode = sessionMode(win, selectedDate);
  const decision = cp ? normDecision(cp.decision_label) : null;
  const nextRun = sessionState?.next?.run;

  const primary = primaryQ.data?.checkpoint?.id === cp?.id ? primaryQ.data : null;
  const cand = primary?.candidate || null;
  const topCandidate = (candQ.data?.candidates || []).filter((c) => !c.is_recalc).sort((a, b) => a.rank - b.rank)[0] || null;
  const nearest = primary?.nearest_failed || (!isEnter(decision) ? topCandidate : null);
  const skew = [...checkpoints].filter((c) => !isScoringOnly(c) && c.iv_skew).sort((a, b) => (a.timestamp_et < b.timestamp_et ? 1 : -1))[0]?.iv_skew;

  const confirm = async () => {
    try {
      const res = await api.confirmPrimary(selectedDate, primaryRun);
      toast.success(`Acknowledged by ${res.acknowledged_by}. No order placed.`);
      qc.invalidateQueries({ queryKey: ["primary", selectedDate, primaryRun] });
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || e.message);
    }
  };

  const statusLine = (
    <span className="sn-row" style={{ gap: "var(--space-2)" }} data-testid="desk-status-line">
      {mode.badge}
      <span>
        <span className="sn-sans-num">{sessionDateLabel(selectedDate)}</span>
        {cp ? <> · Run {cp.run}{viewingCurrent ? "" : " (earlier run)"}</> : null}
        {mode.key === "live" ? null : " · read-only"}
      </span>
      {mode.key === "live" && <UpdatedAt at={cpsQ.dataUpdatedAt} data-testid="desk-updated-at" />}
    </span>
  );

  const runSelector = runs.length > 1 && (
    <SegmentedControl label="Run" value={selectedRun} onChange={pickRun} data-testid="run-tabs" size="sm"
      options={runs.map((c) => ({ value: c.run, label: mobile ? `R${c.run}` : `R${c.run} · ${c.cutoff_et || RUN_META[c.run].time}`, testId: `run-tab-${c.run}` }))} />
  );

  const head = (
    <PageHead
      crumb={["Session desk", <span key="date" className="sn-sans-num">{sessionDateLabel(selectedDate)}</span>]}
      title="Session desk"
      context={statusLine}
      actions={<>{runSelector}<Button variant="ghost" icon="GitCommitVertical" onClick={() => navigate("/checkpoints")}>All checkpoints</Button></>}
    />
  );

  const runControls = can("analyst") && (
    <RunControls date={selectedDate} doneRuns={doneRuns} latestRun={[...doneRuns].sort((a, b) => b - a)[0] || null}
      blocked={isHoliday || isSkipped || noSession} blockedReason={noSession ? "No session for this date." : "Runs are refused for this session."}
      isOwner={can("owner")} risk={risk} onChanged={refresh}
      onStartedLive={(d) => { qc.invalidateQueries({ queryKey: ["sessions-list"] }); setSelectedDate(d); refresh(); }} />
  );

  if (!selectedDate) return <PageLoading label="Loading session" />;

  if (noSession) return (
    <div className="sn-stack" style={{ gap: "var(--space-5)" }} data-testid="dashboard">
      {head}
      <TenAmRecordPanel day={selectedDate} compact />
      <EmptyState icon="CalendarOff" title="No session recorded for this date" data-testid="no-session">
        {win.isToday ? `Nothing has run today yet. The scheduler opens the session at Run 1 (${etAndLocal("08:40", { day: selectedDate })}).` : `Sentinel has no record for ${sessionDateLabel(selectedDate)}.`}
      </EmptyState>
      {win.isToday && runControls}
    </div>
  );

  const loadError = (sessionQ.isError && !session) ? sessionQ : cpsQ.isError && !cpsQ.data ? cpsQ : null;

  return (
    <div className="sn-stack print-page" style={{ gap: "var(--space-5)" }} data-testid="dashboard">
      {tourOn && <GuidedTour steps={TOUR_STEPS} onClose={endTour} />}
      <PrintBar title={`Session desk · ${selectedDate}${cp ? ` · Run ${cp.run}` : ""}`} testId="print-desk-btn" />
      {head}

      {killArmed && <DataBanner kind="kill-armed" data-testid="tonight-kill-banner">Every decision is forced to NO TRADE until an owner disarms it.{session?.kill_reason ? ` Reason: ${session.kill_reason}.` : ""}</DataBanner>}
      {stub && session && <DataBanner kind="stub" data-testid="tonight-stub-banner" />}
      {isHoliday && <DataBanner kind="holiday" data-testid="holiday-empty">No checkpoints run on {sessionDateLabel(selectedDate)}.</DataBanner>}
      {isSkipped && <DataBanner kind="holiday" title="SESSION SKIPPED" data-testid="skipped-banner">Session {selectedDate} was skipped. Runs are refused.</DataBanner>}

      {/* Roadmap Y5 (owner, 2026-10-14): the 10:00 am programme leads the desk; the old rule set follows. */}
      <TenAmRecordPanel day={selectedDate} compact />
      <div className="sn-stack" style={{ gap: 2 }} data-testid="desk-old-rules-head">
        <span className="sn-kicker" role="heading" aria-level={2}>Old rule set · kept for comparison</span>
        <span className="sn-small sn-muted" style={{ maxWidth: "80ch" }}>
          The rules the desk was first built on. Runs 1 to 4 (08:40 to 09:46) gather inputs; Run 5 (10:01) is their last decision of the day.
          No test of these rules found an edge after costs, and ENTER stays off.
        </span>
      </div>

      {loadError ? (
        <QueryError query={loadError} title="The session could not load" what={`the session for ${selectedDate}`} testId="desk-error" />
      ) : cpsQ.isLoading || sessionQ.isLoading ? (
        <PageLoading label="Loading the session desk" />
      ) : !cp ? (
        !isHoliday && (
          <EmptyState icon="GitCommitVertical" title={review ? "Only the post-close review is recorded" : "No decision yet"} data-testid="dashboard-empty">
            {review ? "Run 6 scores the day; it is not a trading decision." : `No checkpoint has run for ${sessionDateLabel(selectedDate)}. Run 1 is scheduled for ${etAndLocal("08:40", { day: selectedDate })}.`}
          </EmptyState>
        )
      ) : (
        <>
          {cpsQ.isError && <QueryError query={cpsQ} title="Refresh failed · showing the last good decision" what="the latest checkpoints" />}
          <DecisionHero decision={decision} run={`R${cp.run}`} time={cp.cutoff_et ? etAndLocal(cp.cutoff_et, { day: selectedDate }) : undefined}
            runId={mobile ? null : `${selectedDate}-R${cp.run}`} compact={mobile || mode.key !== "live"}
            reason={decisionReason({ decision, checkpoint: cp, candidate: nearest, killArmed, killReason: session?.kill_reason, nextRun: viewingCurrent ? nextRun : null, enter, today: win.isToday })}
            {...(isEnter(decision) && cand ? { figure: fmt(cand.p_profit, "prob"), figureLabel: "P(profit) · model" } : {})}
            data-testid="dashboard-decision-pill">
            <div className="sn-stack" style={{ gap: "var(--space-3)", marginTop: "var(--space-3)" }}>
              {enterLine && (
                <p className="sn-small" style={{ margin: 0, color: "var(--text-secondary)", maxWidth: "70ch" }} data-testid="desk-enter-off">
                  <Icon name="CircleMinus" size={14} style={{ marginRight: 6, verticalAlign: "-2px" }} />{enterLine}
                </p>
              )}
              <div className="sn-row" style={{ gap: "var(--space-6)", alignItems: "flex-start" }}>
                <StatCell size="sm" label="Confidence" value={cp.confidence != null ? String(cp.confidence) : null} sub={cp.confidence_band || null} data-testid="stat-confidence" />
                <div className="sn-stat sn-stat-sm" data-testid="stat-path"><span className="sn-stat-k">Path</span><PathChip path={pathOf(cp.path_state)} /></div>
                <StatCell size="sm" label="Evaluated" value={cp.evaluated_at || cp.timestamp_et ? etSyd(cp.evaluated_at || cp.timestamp_et, { seconds: false }) : null} data-testid="stat-cutoff"
                  sub={<>Sydney · <span className="sn-sans-num">{checkpointSchedule(selectedDate, cp.cutoff_et)?.sydney || "—"}</span></>} />
              </div>
              <div className="sn-row" style={{ gap: "var(--space-2)" }}>
                {isLate(cp) && <Badge icon="Clock" className="sn-prov" title={lateLabel(cp)} data-testid="desk-late-badge">LATE · {lateLabel(cp).toLowerCase()}</Badge>}
                {cp.operational_status === "KILL SWITCH" && <SystemBadge kind="kill-armed" data-testid="dashboard-op-status">KILL SWITCH</SystemBadge>}
                {cp.operational_status === "STALE" && <Badge icon="Hourglass" className="sn-prov" data-testid="dashboard-op-status">STALE</Badge>}
                {!viewingCurrent && current && (
                  <Button size="sm" variant="ghost" iconRight="ArrowRight" onClick={() => pickRun(current.run)} data-testid="desk-back-to-current">Current decision · R{current.run}</Button>
                )}
                {review && (
                  <Button size="sm" variant="ghost" iconRight="ArrowRight" onClick={() => navigate("/scoring")} data-testid="tonight-view-scoring">Post-close review · Scoring</Button>
                )}
              </div>
              {cp.contradiction && <span className="sn-small sn-muted"><span className="sn-micro" style={{ marginRight: 6 }}>Contradiction</span>{cp.contradiction}</span>}
            </div>
          </DecisionHero>

          <section id="desk-ticket" className="sn-stack" style={{ gap: "var(--space-4)", scrollMarginTop: "var(--space-16)" }} data-testid="desk-ticket" aria-label={isEnter(decision) ? "Trade ticket" : "Why no trade"}>
            {(cp.safety_blocks || []).length > 0 && (
              <Panel title={<span data-testid="primary-safety-title">Entry safety · blocked</span>} icon="ShieldAlert" data-testid="primary-safety-blocks"
                meta={<span className="sn-num">{cp.safety_blocks.length} block{cp.safety_blocks.length === 1 ? "" : "s"}</span>}>
                <ul className="sn-ready">
                  {cp.safety_blocks.map((block, index) => (
                    <li key={`${block.code}-${index}`} data-testid={`primary-safety-block-${index}`} style={{ gridTemplateColumns: "auto 1fr" }}>
                      <RuleResult result="FAIL">{block.code}</RuleResult>
                      <span style={{ overflowWrap: "anywhere", minWidth: 0 }}>
                        {safetyBlockText(block)}
                        {safetyBlockDetail(block) && <span className="sn-small sn-faint"> {safetyBlockDetail(block)}</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              </Panel>
            )}
            <TicketOrBlock decision={decision} cp={cp} cand={cand} pick={primary?.pick} nearest={nearest} primaryQ={primaryQ} primaryRun={primaryRun} candQ={candQ}
              selectedDate={selectedDate} numState={numState} mobile={mobile} canAck={can("analyst")} onConfirm={confirm} />
          </section>

          {primaryRun && <EntryReadinessChecklist date={selectedDate} run={primaryRun} wide={!mobile} live={win.live && viewingCurrent} />}

          <Panel id="desk-candidates" title={<>Ranked candidates · Run <span className="sn-num">{cp.run}</span></>} icon="ListOrdered" style={{ scrollMarginTop: "var(--space-16)" }} data-testid="desk-candidates"
            meta={<><DataStateBadge state={stub ? "STUB" : "FROZEN"} /><span className="sn-num">{etSyd(cp.timestamp_et, { seconds: false })}</span></>}>
            <CandidateBlotter query={candQ} cp={cp} run={cp.run} stub={stub} mobile={mobile} width={width} />
          </Panel>

          {/* Research only: kept below the candidates so it never reads as part of the decision. */}
          <PaperStrategyNote date={selectedDate} />
        </>
      )}

      {!onboardHidden && session && session.provider !== "polygon" && (
        <Panel title="New here?" icon={GraduationCap} data-testid="onboarding-card"
          actions={<IconButton icon="X" label="Hide the getting-started card" size="sm" onClick={dismissOnboard} data-testid="onboarding-dismiss" />}>
          <div className="sn-row">
            <span className="sn-small sn-muted">Five decision runs each morning ({etAndLocal("08:40", { day: selectedDate })} → {etAndLocal("10:01", { day: selectedDate })}) and a post-close review. Most days: NO TRADE.</span>
            <Button size="sm" iconRight="ArrowRight" onClick={() => navigate("/guide")} data-testid="onboarding-guide">Beginner guide</Button>
            {can("analyst") && <Button size="sm" variant="ghost" onClick={() => { try { localStorage.removeItem("sentinel_tour_done"); } catch { /* private mode */ } setTourOn(true); }} data-testid="onboarding-tour">Tour</Button>}
          </div>
        </Panel>
      )}

      {(cp || review) && (
        <div className="sn-grid-12">
          <Panel className="sn-col-8" title="Checkpoints" icon="GitCommitVertical" style={{ minWidth: 0 }}
            meta={<span><span className="sn-num">{runs.length}</span> decision runs{review ? " · review recorded" : ""}</span>}>
            <div style={{ overflowX: "auto" }}>
              <CheckpointTimeline checkpoints={timelineFrom(checkpoints, current?.run)} loudCurrent={false} orientation={mobile ? "vertical" : "horizontal"} compact={mobile} />
            </div>
          </Panel>
          <Panel className="sn-col-4" title="Next run · data" icon="Timer" style={{ minWidth: 0 }}>
            <div className="sn-stack">
              <NextCheckpoint selectedDate={selectedDate} win={win} doneRuns={doneRuns} review={review} />
              {session?.provider === "polygon" && (
                <>
                  <div className="sn-hr" />
                  <div className="sn-stack" style={{ gap: "var(--space-2)" }} data-testid="live-banner">
                    <span className="sn-small" style={{ fontWeight: 600 }} data-testid="dashboard-source-label">{mode.key === "live" ? "LIVE · SPXW 0DTE · real-time" : `${mode.key === "saved" ? "SAVED SESSION" : "ARCHIVED"} · real-time data when recorded`}</span>
                    <SkewSparkline skew={skewUsable(skew) ? skew : skew ? { suspect: true, suspect_reason: skew.suspect_reason } : null} />
                  </div>
                </>
              )}
            </div>
          </Panel>
        </div>
      )}

      {cp && (
        <Collapsible id="desk-model" title="Model detail" icon="Sigma" testId="desk-model"
          meta={<span>forecast · signals · advisories</span>}>
          <Forecast cp={cp} numState={numState} stub={stub} />
          {cp.actual_open != null && (
            <div className="sn-row" style={{ gap: "var(--space-8)" }} data-testid="desk-open-vs-forecast">
              <StatCell size="md" label="Original forecast open" value={cp.original_forecast_open} kind="index" state={numState} data-testid="stat-forecast-open-orig" />
              <StatCell size="md" label="Actual open" value={cp.actual_open} kind="index" state={numState} data-testid="stat-actual-open" />
              <StatCell size="md" label="Open error" value={cp.open_error} kind="index" signed state={numState} data-testid="stat-open-error" sub="actual − forecast" />
            </div>
          )}
          <SignalExplainer date={selectedDate} run={cp.run} live={win.live && viewingCurrent} />
          <SoftAdvisoryStrip advisories={cp.soft_advisories} />
        </Collapsible>
      )}

      {runControls}
    </div>
  );
}

function Forecast({ cp, numState, stub }) {
  const modeled = pCloseModeled(cp);
  return (
    <div className="sn-stack" style={{ gap: "var(--space-2)" }} data-testid="desk-forecast">
      <span className="sn-row sn-small sn-muted" style={{ gap: "var(--space-2)" }}>R{cp.run} forecast · q10 / q50 / q90 <DataStateBadge state={stub ? "STUB" : "FROZEN"} /></span>
      <div className="sn-forecast" role="group" aria-label="Session forecast">
        <StatCell size="md" label="Open" value={cp.forecast_open} kind="index" state={numState} data-testid="stat-fc-open" />
        <StatCell size="md" label="High" value={cp.forecast_high} kind="index" state={numState} data-testid="stat-fc-high" />
        <StatCell size="md" label="Low" value={cp.forecast_low} kind="index" state={numState} data-testid="stat-fc-low" />
        <StatCell size="md" label="Close q10" value={cp.close_q10} kind="index" state={numState} data-testid="stat-q10" />
        <StatCell size="md" label="Close q50" value={cp.close_q50} kind="index" state={numState} data-testid="stat-q50" />
        <StatCell size="md" label="Close q90" value={cp.close_q90} kind="index" state={numState} data-testid="stat-q90" />
      </div>
      <div className="sn-forecast" role="group" aria-label="Range, stress and close direction">
        <StatCell size="md" label="Expected range" value={cp.expected_range} kind="pts" unit="pts" state={numState} data-testid="stat-range" />
        <StatCell size="md" label="Stress high" value={cp.stress_high} kind="index" state={numState} data-testid="stat-stress-high" />
        <StatCell size="md" label="Stress low" value={cp.stress_low} kind="index" state={numState} data-testid="stat-stress-low" />
        {modeled ? (
          <>
            <StatCell size="md" label="P(up close)" value={cp.p_up_close} kind="prob" state={numState} data-testid="stat-pup" />
            <StatCell size="md" label="P(down close)" value={cp.p_down_close} kind="prob" state={numState} data-testid="stat-pdown" />
          </>
        ) : (
          <StatCell size="md" label="P(up) / P(down)" value="not modelled" data-testid="stat-pclose-unmodeled" sub="fixed at 0.50" />
        )}
      </div>
    </div>
  );
}

/** ENTER → the read-only ticket and payoff. Otherwise → the blocking rule on the nearest failed candidate. */
function TicketOrBlock({ decision, cp, cand, pick, nearest, primaryQ, primaryRun, candQ, selectedDate, numState, mobile, canAck, onConfirm }) {
  const [auditOpen, setAuditOpen] = useState(false);
  const loading = (primaryRun && primaryQ.isLoading) || (!nearest && candQ.isLoading);
  if (loading) return <PageLoading label="Loading the ticket" lines={3} />;
  if (primaryRun && primaryQ.isError && !primaryQ.data) return <QueryError query={primaryQ} title="The primary pick could not load" what={`the primary pick for Run ${primaryRun}`} testId="primary-error" />;

  if (isEnter(decision) && cand) {
    return (
      <div className="sn-grid-12" data-testid="primary-trade-panel">
        <div className="sn-col-7" style={{ minWidth: 0 }}>
          <DeskTradeTicket cand={cand} cp={cp} pick={pick} sessionDate={selectedDate} numState={numState}
            footer={canAck ? (
              <AckButton confirmed={!!pick?.confirmed_by} confirmedAt={pick?.confirmed_at ? etSyd(pick.confirmed_at) : ""} onConfirm={onConfirm} testId="confirm-btn" />
            ) : null} />
          {pick?.confirmed_by && (
            <span className="sn-small sn-muted" data-testid="ack-note">
              <Icon name="ClipboardCheck" size={14} style={{ marginRight: 4, verticalAlign: "-2px" }} />
              Acknowledged by {pick.confirmed_by} · <span className="sn-num">{etSyd(pick.confirmed_at)}</span>
            </span>
          )}
        </div>
        <div className="sn-col-5" style={{ minWidth: 0 }}>
          <Panel title="Payoff at expiry" icon="ChartLine" meta={<span>1 lot · ×100</span>}>
            <PayoffDiagram candidate={cand} showTitle={false} />
          </Panel>
        </div>
      </div>
    );
  }

  const rule = cp.blocking_rule || nearest?.blocking_rule;
  const passedAll = !rule && enterOffPassed({ decision, checkpoint: cp, candidate: nearest });
  return (
    <div className="sn-stack" style={{ gap: "var(--space-3)" }} data-testid="no-trade-panel">
      {rule ? (
        <BlockingRuleCallout {...blockingProps(rule, nearest, cp)} onEvidence={nearest ? () => setAuditOpen(true) : undefined} data-testid="tonight-blocking-callout" />
      ) : (
        <p className="sn-small" style={{ margin: 0, color: passedAll ? "var(--text-primary)" : undefined, fontWeight: passedAll ? 600 : undefined }} data-testid="tonight-blocking-callout">
          {noBlockingRuleText({ decision, checkpoint: cp, candidate: nearest })}
        </p>
      )}
      {nearest && (
        <Panel title={passedAll ? "Top candidate · passed every rule" : "Nearest failed candidate"} icon="Crosshair" data-testid="primary-nearest-panel"
          meta={<span className="sn-num">rank {nearest.rank ?? 1} · {shortId(nearest.candidate_id)}</span>}
          actions={<Button size="sm" variant="ghost" icon={auditOpen ? "ChevronUp" : "ChevronDown"} aria-expanded={auditOpen} aria-controls="desk-nearest-audit" onClick={() => setAuditOpen((o) => !o)} data-testid="nearest-audit-toggle">{auditOpen ? "Hide audit" : "Show audit"}</Button>}>
          <div className="sn-stack">
            <div className="sn-row" style={{ gap: "var(--space-6)" }}>
              <div className="sn-stat sn-stat-sm"><span className="sn-stat-k">Structure</span><span style={{ fontWeight: 600 }}>{nearest.structure_name} · <span className="sn-num">{fmt(nearest.short_strike, "strike")} / {fmt(nearest.long_strike, "strike")}</span></span></div>
              <StatCell size="sm" label={nearest.is_credit ? "Net credit" : "Net debit"} value={Math.abs(Number(nearest.net_price) || 0)} kind="price" state={numState} />
              <StatCell size="sm" label="Net EV / max risk" value={nearest.ev_pct != null ? nearest.ev_pct * 100 : null} kind="pct" signed state={numState}
                sub={cp.e1_floor != null ? <span>needs ≥ {fmt(cp.e1_floor * 100, "pct", { signed: true })}</span> : null} />
              <StatCell size="sm" label="P(profit) · model" value={nearest.p_profit} kind="prob" state={numState} />
              <StatCell size="sm" label="Rules failed" value={nearest.n_fail_total != null ? String(nearest.n_fail_total) : "—"} />
            </div>
            {auditOpen && (
              <div className="sn-grid-12" id="desk-nearest-audit">
                <div className={mobile ? "sn-col-12" : "sn-col-7"} style={{ minWidth: 0 }}>
                  <AuditRow rules={nearest.rules} candidate={nearest} checkpoint={cp} stacked={mobile} id="primary-nearest-audit" />
                </div>
                <div className={mobile ? "sn-col-12" : "sn-col-5"} style={{ minWidth: 0 }}>
                  <PayoffDiagram candidate={nearest} />
                </div>
              </div>
            )}
          </div>
        </Panel>
      )}
      {!nearest && <p className="sn-small sn-faint" style={{ margin: 0 }}>No candidate was generated at Run {cp.run}.</p>}
    </div>
  );
}
